/**
 * What the context panel knows about the rental and hardware a session is
 * about, read from ant by id (so it works for any system the token can see,
 * unlike the REST rental list, which is scoped to the selected system).
 *
 * The session's own rental wins; a general session uses the rental the rider
 * last wrote about. Two pieces of hardware are kept apart: the rental's own
 * (what "End rental" and the rental section are about) and the one the
 * rider's messages name, which may be another unit altogether.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ChatContextHardware,
  ChatContextRental,
  fetchHardwareContext,
  fetchRentalContext,
} from './api';

export interface ConversationContext {
  rental: ChatContextRental | null;
  /** The rental's hardware (rental.hardware); null without a rental. */
  rentalHardware: ChatContextHardware | null;
  /** The hardware the rider's messages name, when it is not the rental's. */
  messageHardware: ChatContextHardware | null;
  loading: boolean;
  /** Set when ant could not be read; the panel says so instead of "none". */
  error: string | null;
  reload: () => void;
}

export function useConversationContext(
  rentalId: string | null,
  hardwareId: string | null
): ConversationContext {
  const [rental, setRental] = useState<ChatContextRental | null>(null);
  const [messageHardware, setMessageHardware] = useState<ChatContextHardware | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRental(null);
    setMessageHardware(null);
    setError(null);
    if (!rentalId && !hardwareId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const nextRental = rentalId ? await fetchRentalContext(rentalId) : null;
        const nextMessageHardware =
          hardwareId && hardwareId !== nextRental?.hardware?.id
            ? await fetchHardwareContext(hardwareId)
            : null;
        if (cancelled) return;
        setRental(nextRental);
        setMessageHardware(nextMessageHardware);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rentalId, hardwareId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    rental,
    rentalHardware: rental?.hardware ?? null,
    messageHardware,
    loading,
    error,
    reload,
  };
}
